import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  BookOpen,
  Package,
  Users,
  ShoppingBag,
  Truck,
  Megaphone,
  FileText,
  Mail,
  Settings,
  MessageSquare,
  Star,
  ScrollText,
  LogOut,
  Menu,
  X,
  ArrowLeft,
  Shield,
  UserCog,
  Radio,
  Images,
  Ticket,
  HelpCircle,
  Brain,
  Book,
  ClipboardList,
  AlertTriangle,
  Clapperboard,
  Smartphone,
} from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import Seo from "@/components/seo/Seo";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/authStore";
import { useLogout } from "@/features/auth/hooks";
import { useAdminLockdownStatus } from "@/features/admin/hooks";

const NAV_GROUPS: Array<{
  label: string;
  items: Array<{ to: string; label: string; icon: typeof LayoutDashboard; end?: boolean }>;
}> = [
  {
    label: "Genel",
    items: [{ to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true }],
  },
  {
    label: "Eğitim",
    items: [
      { to: "/admin/kurslar", label: "Kurslar", icon: BookOpen },
      { to: "/admin/paketler", label: "Paketler", icon: Package },
      { to: "/admin/kitaplar", label: "Kitap Yönetimi", icon: Book },
      { to: "/admin/canli-dersler", label: "Canlı Dersler", icon: Radio },
      { to: "/admin/ucretsiz-quizler", label: "Denemeler", icon: HelpCircle },
      { to: "/admin/deneme-sonuclari", label: "Kimler Ne Çözdü", icon: ClipboardList },
      { to: "/admin/ai-kitapcik", label: "🤖 YZ Kitapçık Üretici", icon: Brain },
      { to: "/admin/ai-konu-anlatimi", label: "🤖 YZ Konu Anlatımı (eski)", icon: Brain },
      {
        to: "/admin/jsps-grounded-konu-anlatimi",
        label: "YZ Grounded Konu Anlatımı",
        icon: Brain,
      },
      { to: "/admin/video-otomasyon", label: "Video Otomasyonu", icon: Clapperboard },
    ],
  },
  {
    label: "Müşteri",
    items: [
      { to: "/admin/kullanicilar", label: "Kullanıcılar", icon: Users },
      { to: "/admin/siparisler", label: "Siparişler", icon: ShoppingBag },
      { to: "/admin/kargo", label: "Kargo Bilgileri", icon: Truck },
      { to: "/admin/indirim-kodlari", label: "İndirim Kodları", icon: Ticket },
      { to: "/admin/iletisim", label: "İletişim", icon: MessageSquare },
    ],
  },
  {
    label: "İçerik",
    items: [
      { to: "/admin/anasayfa-slayt", label: "Anasayfa Slaytları", icon: Images },
      { to: "/admin/blog", label: "Blog", icon: FileText },
      { to: "/admin/duyurular", label: "Duyurular", icon: Megaphone },
      { to: "/admin/yorumlar", label: "Yorumlar", icon: Star },
    ],
  },
  {
    label: "Sistem",
    items: [
      { to: "/admin/uygulama", label: "Uygulama yönetimi", icon: Smartphone },
      { to: "/admin/yasal", label: "Yasal Metinler", icon: ScrollText },
      { to: "/admin/email-sablonlari", label: "E-posta Şablonları", icon: Mail },
      { to: "/admin/ayarlar", label: "Ayarlar", icon: Settings },
      { to: "/admin/profilim", label: "Profilim", icon: UserCog },
    ],
  },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { data: lockdown } = useAdminLockdownStatus();
  const lockdownActive = !!lockdown?.active;

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Seo
        title="Admin Paneli | Mersa Akademi"
        description="Mersa Akademi yönetim paneli."
        path="/admin"
        noindex
      />
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-30 w-64 transform border-r border-border bg-brand-900 text-white transition-transform lg:static lg:translate-x-0 overflow-y-auto",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex h-16 items-center justify-between border-b border-brand-800 px-4">
          <Link to="/admin" className="flex items-center gap-2 text-white">
            <Shield className="h-6 w-6 text-brand-300" />
            <span className="font-bold">Admin Paneli</span>
          </Link>
          <button
            className="lg:hidden text-brand-200"
            onClick={() => setMobileOpen(false)}
            aria-label="Menüyü kapat"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex flex-col gap-1 p-3">
          {NAV_GROUPS.map((group) => (
            <div key={group.label} className="mt-2 first:mt-0">
              <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-brand-300">
                {group.label}
              </div>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-brand-700 text-white"
                        : "text-brand-200 hover:text-white hover:bg-brand-800"
                    )
                  }
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
          <div className="my-3 border-t border-brand-800" />
          <NavLink
            to="/panel"
            className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-brand-200 hover:text-white hover:bg-brand-800"
          >
            <ArrowLeft className="h-4 w-4" />
            Öğrenci Paneli
          </NavLink>
          <button
            onClick={() =>
              logout.mutate(undefined, { onSuccess: () => navigate("/") })
            }
            className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-brand-200 hover:text-white hover:bg-brand-800"
          >
            <LogOut className="h-4 w-4" />
            Çıkış Yap
          </button>
        </nav>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-20 bg-black/40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      <div className="flex flex-1 flex-col">
        <header className="sticky top-0 z-10 flex h-16 items-center justify-between border-b border-border bg-white px-4 lg:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Menüyü aç"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex-1" />
          {user && (
            <Link
              to="/admin/profilim"
              className="flex items-center gap-3 text-sm hover:opacity-80 transition-opacity"
            >
              <div className="text-right">
                <div className="font-medium">
                  {user.firstName} {user.lastName}
                </div>
                <div className="text-xs text-muted-foreground">ADMIN</div>
              </div>
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-700 text-white font-bold">
                {user.firstName?.[0]?.toUpperCase()}
              </div>
            </Link>
          )}
        </header>
        <main className="flex-1 p-4 lg:p-6">
          {lockdownActive && (
            <div
              role="alert"
              className="mb-4 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-950"
            >
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <div className="text-sm">
                <div className="font-semibold">Admin paneli kilitli</div>
                <p className="mt-0.5 text-amber-900/80">
                  {lockdown?.mode === "full"
                    ? "Güvenlik kilidi aktif: tüm admin işlemleri engelleniyor."
                    : "Güvenlik kilidi aktif: kayıt, silme ve güncelleme yapılamaz. Okuma devam eder."}
                </p>
              </div>
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
